<template>
  <div class="bohr-model-viz">
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$mvr = n * h/2π$</div>
    </div>

    <div class="model-container">
      <svg width="100%" height="400" viewBox="0 0 400 400">
        <!-- 背景星空感 - 优化：使用预生成的星星坐标避免重绘闪烁 -->
        <circle v-for="(star, index) in stars" :key="'star-'+index" :cx="star.x" :cy="star.y" :r="star.r" fill="#fff" opacity="0.2" />

        <!-- 原子核 -->
        <g class="nucleus">
          <circle cx="200" cy="200" r="12" fill="url(#nucleusGradient)" stroke="#ff4757" stroke-width="1" />
          <circle cx="200" cy="200" r="25" fill="#ff4757" opacity="0.1">
            <animate attributeName="r" values="20;30;20" dur="3s" repeatCount="indefinite" />
          </circle>
          <text x="200" y="204" text-anchor="middle" fill="#fff" font-size="10" font-weight="bold">Ze+</text>
        </g>

        <!-- 量子化轨道 -->
        <g v-for="level in 4" :key="'orbit-'+level">
          <circle cx="200" cy="200" :r="level * level * 20" fill="none" :stroke="n === level ? '#00d2ff' : '#333'" :stroke-width="n === level ? 2 : 1" :stroke-dasharray="n === level ? 'none' : '4,4'" class="orbit-line" />
          <text :x="200" :y="200 - (level * level * 20) - 5" fill="#555" font-size="10" text-anchor="middle">n={{ level }}</text>
        </g>

        <!-- 驻波示意 (量子化条件的解释) -->
        <path v-if="showWave" :d="standingWavePath" fill="none" stroke="rgba(0, 210, 255, 0.4)" stroke-width="1.5" class="standing-wave" />

        <!-- 电子 -->
        <g :transform="electronTransform">
          <circle r="6" fill="#00d2ff" class="electron-core">
            <animate attributeName="r" values="5;7;5" dur="1s" repeatCount="indefinite" />
          </circle>
          <circle r="12" fill="#00d2ff" opacity="0.2">
             <animate attributeName="opacity" values="0.1;0.4;0.1" dur="1.5s" repeatCount="indefinite" />
          </circle>
        </g>

        <defs>
          <radialGradient id="nucleusGradient">
            <stop offset="10%" stop-color="#ff6b6b" />
            <stop offset="90%" stop-color="#ee5253" />
          </radialGradient>
        </defs>
      </svg>
    </div>

    <div class="control-panel">
      <div class="labeled-slider">
        <label>量子数 n:</label>
        <el-slider v-model="n" :min="1" :max="4" :step="1" show-stops></el-slider>
      </div>
      <div class="toggles">
        <el-switch v-model="showWave" active-text="显示物质波驻波状态"></el-switch>
      </div>
    </div>

    <div class="theory-card">
      <h4>玻尔假设：</h4>
      <ul>
        <li><strong>定态假设</strong>：原子存在具有确定能量的状态，不向外辐射能量。</li>
        <li><strong>跃迁假设</strong>：高能态 Em 到低能态 En 辐射频率为 v = (Em - En)/h 的光子。</li>
        <li><strong>角动量量子化</strong>：mvr = n * h/2π，轨道半径不连续。</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BohrModelViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      n: 1,
      angle: 0,
      showWave: true,
      animationId: null,
      stars: Array.from({ length: 20 }, () => ({
        x: Math.random() * 400,
        y: Math.random() * 400,
        r: 0.5 + Math.random() * 0.5
      }))
    }
  },
  computed: {
    electronRadius() {
      return this.n * this.n * 20;
    },
    electronTransform() {
      const rad = (this.angle * Math.PI) / 180;
      const x = 200 + Math.cos(rad) * this.electronRadius;
      const y = 200 + Math.sin(rad) * this.electronRadius;
      return `translate(${x}, ${y})`;
    },
    standingWavePath() {
      const r = this.electronRadius;
      const points = 100;
      const peaks = this.n * 2;
      const amplitude = 5;
      let d = "";
      for(let i = 0; i <= points; i++) {
        const theta = (i / points) * 2 * Math.PI;
        const radius = r + Math.sin(theta * peaks) * amplitude;
        const x = 200 + Math.cos(theta) * radius;
        const y = 200 + Math.sin(theta) * radius;
        d += (i === 0 ? "M " : "L ") + x + " " + y;
      }
      return d + " Z";
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    if (this.animationId) cancelAnimationFrame(this.animationId);
  },
  methods: {
    animate() {
      const speed = 2 / this.n;
      this.angle = (this.angle + speed) % 360;
      this.animationId = requestAnimationFrame(this.animate);
    }
  }
}
</script>

<style scoped lang="scss">
.bohr-model-viz { width: 100%; color: #fff; display: flex; flex-direction: column; gap: 15px; }
.viz-header { display: flex; justify-content: space-between; padding: 10px; background: rgba(255,255,255,0.05); border-radius: 4px; .brief { font-size: 14px; font-weight: bold; } .formula-chip { color: #00d2ff; background: rgba(0,210,255,0.1); padding: 4px 12px; border-radius: 8px; font-weight: bold; } }
.model-container { background: radial-gradient(circle at center, #1a1a2e 0%, #0a0a0a 100%); border-radius: 8px; }
.orbit-line { transition: all 0.5s ease; }
.standing-wave { filter: drop-shadow(0 0 3px #00d2ff); stroke-dasharray: 2,2; animation: waveRotate 10s linear infinite; }
@keyframes waveRotate { from { transform: rotate(0deg); transform-origin: 200px 200px; } to { transform: rotate(360deg); transform-origin: 200px 200px; } }
.control-panel { display: flex; flex-direction: column; gap: 10px; padding: 15px; background: rgba(255,255,255,0.02); border-radius: 8px; .labeled-slider { display: flex; align-items: center; gap: 20px; label { font-size: 0.9rem; width: 80px; } .el-slider { flex: 1; } } }
.theory-card { padding: 15px; background: rgba(0, 210, 255, 0.05); border-radius: 4px; border-left: 2px solid #00d2ff; h4 { margin: 0 0 10px 0; font-size: 1rem; color: #00d2ff; } ul { margin: 0; padding-left: 20px; font-size: 0.85rem; color: #aaa; } li { margin-bottom: 5px; } }
</style>
